import React from 'react';
import { Box, SxProps, Typography } from '@mui/material';
import { Checkbox } from '@europrocurement/flexy-components';
import { type FormalitiesAndDocs } from '@europrocurement/l2d-modules/modules/Orders';
import { DOC_TYPE_REQUIRED } from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import { DisplayedDocument } from '../../../providers/types';
import DocumentItem from '../DocumentItem/DocumentItem';
import useDossierFile from '../../../hooks/useDossierFile';

export type FormalitiesAndDocumentsSelectorsFormProps = {
    dossFormalitiesAndDocs: FormalitiesAndDocs[];
    // Formalities
    selectedFormalities: FormalitiesAndDocs[];
    setSelectedFormalities: React.Dispatch<React.SetStateAction<FormalitiesAndDocs[]>>;
    selectedDocuments: DisplayedDocument[];
    setSelectedDocuments: React.Dispatch<React.SetStateAction<DisplayedDocument[]>>;
    //
    sxWrapper?: SxProps;
};

const FormalitiesAndDocumentsSelectorsForm: React.FunctionComponent<FormalitiesAndDocumentsSelectorsFormProps> =
    function ({
        dossFormalitiesAndDocs,
        selectedFormalities,
        setSelectedFormalities,
        selectedDocuments,
        setSelectedDocuments,
        sxWrapper,
    }) {
        const { enqueueSnackbar } = useSnackbar();
        const { handleShow } = useDossierFile({
            typeDoc: DOC_TYPE_REQUIRED,
            setIsSubmitting: () => {},
        });

        // Select ou remove selected formality and related documents
        const handleSelectFormality = (formality: FormalitiesAndDocs) => {
            if (formality.id) {
                const formalityDocs = formality.displayedDocs;
                if (selectedFormalities.some((slctForma) => slctForma.id === formality.id)) {
                    // Remove formality from selectedFormalities
                    setSelectedFormalities((formalitiesState) =>
                        formalitiesState.filter(
                            (formalityState) => formalityState.id !== formality.id,
                        ),
                    );
                    // Remove relatedDocs from formality
                    formalityDocs?.map((doc) => {
                        // Check if the doc is not used on another formality
                        const formalitiesWithDoc = selectedFormalities.filter((forma) =>
                            forma.displayedDocs?.some((slctDoc) => slctDoc.id === doc.id),
                        );

                        // Only this formality has the doc, remove it from the selectedDocs
                        if (formalitiesWithDoc.length === 1) {
                            setSelectedDocuments((docsState) =>
                                docsState.filter((docState) => docState.id !== doc.id),
                            );
                        }
                        return true;
                    });
                } else {
                    // Add formality in selectedFormalities
                    setSelectedFormalities((formalitiesState) => [...formalitiesState, formality]);
                    // Add relatedDocs from formality
                    formalityDocs?.map((doc) => setSelectedDocuments((docs) => [...docs, doc]));
                }
            }
        };

        // Select ou remove selected document
        const handleSelectDocument = (doc: DisplayedDocument) => {
            if (doc.documentTypeId) {
                if (
                    selectedDocuments.some(
                        (slctDocs) => slctDocs.documentTypeId === doc.documentTypeId,
                    )
                ) {
                    setSelectedDocuments((docsState) =>
                        docsState.filter(
                            (docState) => docState.documentTypeId !== doc.documentTypeId,
                        ),
                    );
                } else {
                    setSelectedDocuments((docs) => [...docs, doc]);
                }
            }
        };

        // check or uncheck selected formality reinitForm property
        const handleCheckFormalityForm = (formality: FormalitiesAndDocs) => {
            if (formality.id) {
                // update formality from selectedFormalities
                setSelectedFormalities(
                    selectedFormalities.map((formalityState) => {
                        if (formalityState.id === formality.id) {
                            return {
                                ...formalityState,
                                reinitForm: !formalityState.reinitForm,
                            };
                        }
                        return formalityState;
                    }),
                );
            }
        };

        const handleShowDocument = async (doc: DisplayedDocument) => {
            if (doc.fichierId) {
                handleShow(doc.label, doc.fichierId, doc.id);
            } else {
                enqueueSnackbar(`Une erreur est survenue lors de la récupération du fichier`, {
                    variant: 'error',
                });
            }
        };

        return (
            <Box
                sx={{
                    padding: '0',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px 0',
                    ...sxWrapper,
                }}
            >
                {dossFormalitiesAndDocs.map((formality) => (
                    <Box
                        key={`formality-checkBox-${formality.id}`}
                        sx={{ display: 'grid', gap: '8px' }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                width: '100%',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Checkbox
                                    checked={
                                        selectedFormalities &&
                                        selectedFormalities.some(
                                            (slctForma) => slctForma.id === formality.id,
                                        )
                                    }
                                    onChange={() => handleSelectFormality(formality)}
                                    data-testid="test-formality-checkBox"
                                />

                                <Typography>{formality.libelle}</Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <Checkbox
                                    checked={
                                        (selectedFormalities &&
                                            selectedFormalities.find((sf) => sf.id === formality.id)
                                                ?.reinitForm) ||
                                        false
                                    }
                                    onChange={() => handleCheckFormalityForm(formality)}
                                    data-testid="test-formality-form-checkBox"
                                />
                                <Typography>Réinitialiser le formulaire</Typography>
                            </Box>
                        </Box>

                        {formality.displayedDocs &&
                            formality.displayedDocs.map((doc) => (
                                <Box
                                    key={`${formality.id}_${doc.id}`}
                                    sx={{
                                        display: 'flex',
                                        marginLeft: '5%',
                                        width: '95%',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Checkbox
                                        checked={selectedDocuments.some(
                                            (slctDocs) =>
                                                slctDocs.documentTypeId === doc.documentTypeId,
                                        )}
                                        onChange={() => handleSelectDocument(doc)}
                                        data-testid="test-document-checkBox"
                                    />
                                    <DocumentItem
                                        {...doc}
                                        sx={{ width: '100%' }}
                                        uploading={false}
                                        itemActions={
                                            doc.fichierId
                                                ? {
                                                      onShow: () => handleShowDocument(doc),
                                                      showTooltip: 'Afficher le document',
                                                  }
                                                : undefined
                                        }
                                    />
                                </Box>
                            ))}
                    </Box>
                ))}
            </Box>
        );
    };

export default FormalitiesAndDocumentsSelectorsForm;
